import React from "react";
import { motion } from "framer-motion";
import { Users, Award, ShieldCheck, Video, Calendar, Sparkles, TrendingUp, DollarSign, BrainCircuit, Activity } from "lucide-react";

export default function SaasHero({ onBookDemo, onStartTrial }) {
  // Animations setup
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
  };

  const floatVariants = (duration, yOffset) => ({
    animate: {
      y: [0, yOffset, 0],
      transition: {
        duration: duration,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  });

  return (
    <section className="relative pt-24 pb-32 overflow-hidden bg-white dark:bg-slate-950">
      {/* Background Gradients & Effects */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[60%] aspect-square rounded-full bg-gradient-to-tr from-violet-600/10 to-blue-500/10 blur-[120px]" />
        <div className="absolute -bottom-[20%] -right-[10%] w-[60%] aspect-square rounded-full bg-gradient-to-br from-cyan-500/10 to-violet-600/10 blur-[120px]" />
        <div className="absolute inset-0 bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] dark:bg-[radial-gradient(#1e293b_1px,transparent_1px)] [background-size:24px_24px] opacity-40" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          
          {/* Hero Content */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="lg:col-span-6 space-y-8"
          >
            <motion.div 
              variants={itemVariants}
              className="inline-flex items-center gap-2 px-4 py-2 bg-violet-50 dark:bg-violet-950/40 border border-violet-100 dark:border-violet-900/50 rounded-full text-violet-600 dark:text-violet-400 text-sm font-bold shadow-sm"
            >
              <Sparkles size={16} className="animate-pulse" />
              <span>AI-Powered Multi-Tenant LMS</span>
            </motion.div>

            <motion.h1 
              variants={itemVariants}
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 dark:text-white leading-[1.1] tracking-tight"
            >
              The Complete AI-Powered LMS for{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 via-blue-600 to-cyan-500">
                Schools, Colleges & Coaching
              </span>
            </motion.h1>

            <motion.p 
              variants={itemVariants}
              className="text-lg sm:text-xl text-slate-600 dark:text-slate-300 leading-relaxed font-medium"
            >
              Launch your institute's digital campus with online exams, subjective exams, AI tutors, live classes, analytics, and complete institute management.
            </motion.p>

            <motion.div 
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={onBookDemo}
                className="px-8 py-4 bg-gradient-to-r from-violet-600 to-blue-600 text-white font-black rounded-2xl shadow-xl shadow-blue-500/20 hover:scale-105 active:scale-98 transition-all text-lg"
              >
                Book Demo
              </button>
              <button
                onClick={onStartTrial}
                className="px-8 py-4 bg-white dark:bg-slate-900 text-slate-800 dark:text-white font-black rounded-2xl border border-slate-200 dark:border-slate-800 hover:border-violet-600 dark:hover:border-violet-400 transition-colors shadow-sm hover:shadow-lg text-lg"
              >
                Start Free Trial
              </button>
            </motion.div>

            {/* Quick social trust */}
            <motion.div 
              variants={itemVariants}
              className="pt-6 flex items-center gap-4 border-t border-slate-100 dark:border-slate-800"
            >
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) => (
                  <img
                    key={i}
                    src={`https://i.pravatar.cc/80?img=${i + 15}`}
                    alt="User avatar"
                    className="w-10 h-10 rounded-full border-2 border-white dark:border-slate-950 object-cover"
                  />
                ))}
              </div>
              <p className="text-sm font-bold text-slate-500 dark:text-slate-400">
                Trusted by <span className="text-violet-600 dark:text-violet-400 font-extrabold">100+ Institutes</span> and <span className="text-blue-600 dark:text-blue-400 font-extrabold">50,000+ Students</span>
              </p>
            </motion.div>
          </motion.div>

          {/* Hero Visual Mockup */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-6 relative h-[580px] w-full hidden md:block"
          >
            {/* Main Dashboard Screen (Glassmorphism) */}
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[95%] h-[85%] bg-slate-900/95 dark:bg-slate-950/90 rounded-[2.5rem] shadow-2xl border border-slate-800/80 overflow-hidden flex flex-col p-5">
              
              {/* Fake Window bar */}
              <div className="flex items-center gap-2 mb-6 border-b border-slate-800 pb-3">
                <div className="w-3 h-3 rounded-full bg-rose-500/80" />
                <div className="w-3 h-3 rounded-full bg-amber-500/80" />
                <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
                <div className="ml-4 text-xs font-bold text-slate-500 tracking-wider">Skillzy Institute OS</div>
              </div>

              {/* Grid content inside Dashboard */}
              <div className="flex-1 grid grid-cols-3 gap-4 overflow-hidden">
                
                {/* Column 1 - Users & Live Feed */}
                <div className="col-span-1 space-y-4">
                  <div className="bg-slate-800/50 rounded-2xl p-4 border border-slate-700/30">
                    <p className="text-xs font-bold text-slate-400 uppercase">Students Active</p>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-xl font-black text-white">4,289</span>
                      <span className="text-[10px] text-emerald-400 font-bold">+12%</span>
                    </div>
                  </div>

                  <div className="bg-slate-800/50 rounded-2xl p-4 border border-slate-700/30 flex-1">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-bold text-slate-400 uppercase">Live Feed</p>
                      <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                    </div>
                    <div className="space-y-3 text-[10px] text-slate-300">
                      <div className="flex items-start gap-2 border-l border-violet-500 pl-2">
                        <div>
                          <p className="font-bold">Rahul S. (Student)</p>
                          <p className="text-[8px] text-slate-500">Submitted Chemistry MCQ</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2 border-l border-blue-500 pl-2">
                        <div>
                          <p className="font-bold">Dr. Mehta (Teacher)</p>
                          <p className="text-[8px] text-slate-500">Evaluated Subjective Paper</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2 border-l border-cyan-500 pl-2">
                        <div>
                          <p className="font-bold">AI Planner</p>
                          <p className="text-[8px] text-slate-500">Suggested Physics study path</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Column 2 - Main Center (Exams & AI Tutor) */}
                <div className="col-span-2 space-y-4">
                  {/* Revenue / Analytics mini-chart */}
                  <div className="bg-slate-800/50 rounded-2xl p-4 border border-slate-700/30">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase">Monthly Revenue</p>
                        <p className="text-lg font-black text-white">₹9,84,500</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-slate-400 uppercase font-mono">LMS Profit</p>
                        <p className="text-sm font-bold text-emerald-400">92%</p>
                      </div>
                    </div>
                    {/* SVG mini chart path */}
                    <div className="h-10 w-full mt-3">
                      <svg viewBox="0 0 100 30" className="w-full h-full">
                        <defs>
                          <linearGradient id="glow" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.4"/>
                            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0"/>
                          </linearGradient>
                        </defs>
                        <path d="M0,25 Q15,10 30,20 T60,5 T90,12 T100,10" fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" />
                        <path d="M0,25 Q15,10 30,20 T60,5 T90,12 T100,10 L100,30 L0,30 Z" fill="url(#glow)" />
                      </svg>
                    </div>
                  </div>

                  {/* AI Tutor Card */}
                  <div className="bg-gradient-to-br from-violet-900/30 to-blue-900/30 rounded-2xl p-4 border border-violet-500/20 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-violet-600/20 text-violet-400 flex items-center justify-center shrink-0">
                      <BrainCircuit size={18} />
                    </div>
                    <div>
                      <p className="text-xs font-black text-white">AI Classroom Assistant</p>
                      <p className="text-[10px] text-violet-200 mt-1 leading-relaxed">
                        "Calculus Quiz drafted based on class performance. Click to distribute to 180 students."
                      </p>
                      <button className="mt-2.5 px-3 py-1 bg-violet-600 text-[10px] text-white font-bold rounded-lg hover:bg-violet-700 transition-colors">
                        Distribute Exam
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Floating Card 1: Students */}
            <motion.div 
              variants={floatVariants(4.5, -12)}
              animate="animate"
              className="absolute -top-6 left-10 bg-white dark:bg-slate-900 p-4 rounded-2xl shadow-xl flex items-center gap-3 border border-slate-100 dark:border-slate-800"
            >
              <div className="w-10 h-10 bg-violet-100 dark:bg-violet-950 text-violet-600 dark:text-violet-400 rounded-full flex items-center justify-center">
                <Users size={20} />
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Total Enrolled</p>
                <p className="text-base font-black text-slate-800 dark:text-white">50,000+ Students</p>
              </div>
            </motion.div>

            {/* Floating Card 2: AI Exam Generator */}
            <motion.div 
              variants={floatVariants(5.5, 15)}
              animate="animate"
              className="absolute top-24 -right-8 bg-slate-900/90 backdrop-blur-md p-4 rounded-2xl shadow-xl flex items-center gap-3 border border-slate-800"
            >
              <div className="w-10 h-10 bg-gradient-to-r from-violet-600 to-blue-600 text-white rounded-full flex items-center justify-center animate-pulse">
                <BrainCircuit size={20} />
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Smart Assist</p>
                <p className="text-base font-black text-white">AI Exam Generator</p>
              </div>
            </motion.div>

            {/* Floating Card 3: Uptime */}
            <motion.div 
              variants={floatVariants(5, -10)}
              animate="animate"
              className="absolute bottom-16 -left-12 bg-white dark:bg-slate-900 p-4 rounded-2xl shadow-xl flex items-center gap-3 border border-slate-100 dark:border-slate-800"
            >
              <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center">
                <ShieldCheck size={20} />
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Service Status</p>
                <p className="text-base font-black text-slate-800 dark:text-white">99.9% Uptime</p>
              </div>
            </motion.div>

            {/* Floating Card 4: Live Classes */}
            <motion.div 
              variants={floatVariants(4.8, 12)}
              animate="animate"
              className="absolute -bottom-8 right-16 bg-white dark:bg-slate-900 p-4 rounded-2xl shadow-xl flex items-center gap-3 border border-slate-100 dark:border-slate-800"
            >
              <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-950 text-cyan-600 dark:text-cyan-400 rounded-full flex items-center justify-center">
                <Video size={20} />
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Active Rooms</p>
                <p className="text-base font-black text-slate-800 dark:text-white">Live Classes</p>
              </div>
            </motion.div>

          </motion.div>

        </div>
      </div>
    </section>
  );
}
